import React, { useState } from "react";

// Rating scale per v2026.05 spec: Label + full definition shown on hover.
export const RATING_SCALE = [
  {
    n: 5,
    label: "Exceptional",
    short: "EXCEPTIONAL",
    def: "Performance consistently and significantly exceeds all expectations for this task. The employee demonstrates outstanding expertise, often going above and beyond the required standard. Serves as a role model or benchmark for others.",
  },
  {
    n: 4,
    label: "Exceeds Expectations",
    short: "EXCEEDS",
    def: "Performance frequently surpasses the required standard for this task. The employee demonstrates a strong grasp of the task and regularly delivers outcomes beyond what is expected.",
  },
  {
    n: 3,
    label: "Meets Expectations",
    short: "MEETS",
    def: "Performance consistently meets the required standard for this task. The employee reliably completes the task to the level expected of someone in this role. This is a solid and satisfactory rating.",
  },
  {
    n: 2,
    label: "Needs Improvement",
    short: "NEEDS IMPROVEMENT",
    def: "Performance partially meets the required standard but falls short in some areas. The employee requires guidance, support, or development to consistently meet expectations for this task.",
  },
  {
    n: 1,
    label: "Unsatisfactory",
    short: "UNSATISFACTORY",
    def: "Performance does not meet the required standard for this task. Significant improvement is needed and a formal performance improvement plan may be required.",
  },
];

const BY_N = Object.fromEntries(RATING_SCALE.map((r) => [r.n, r]));

// Minimum characters required in the evidence notes so the rating is
// substantiated. Short enough to not frustrate, long enough to force a real
// explanation ("good job" is 8 chars and should be rejected).
export const MIN_NOTE_CHARS = 20;

/**
 * Small inline tooltip that appears on hover/focus. Positioned above the
 * trigger so it doesn't shift the row layout. Keyboard-accessible.
 */
function InfoTooltip({ title, body, children, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {children}
      {open && (
        <span
          role="tooltip"
          data-testid={testId}
          className="absolute z-30 left-1/2 -translate-x-1/2 bottom-full mb-2 w-72 cal-card p-3 text-left shadow-lg"
          style={{ background: "#111", color: "#fff", borderColor: "#111" }}
        >
          <span className="block font-mono-cal text-[10px] uppercase tracking-widest" style={{ color: "#FF4F00" }}>{title}</span>
          <span className="block text-xs leading-relaxed mt-1" style={{ color: "#fff" }}>{body}</span>
        </span>
      )}
    </span>
  );
}

export function RatingScaleLegend() {
  // Display in ascending order (1→5) in the legend for readability
  const asc = [...RATING_SCALE].sort((a, b) => a.n - b.n);
  return (
    <div className="cal-card p-4 mb-6 bg-[#F4F4F0]">
      <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-2">
        Rating scale · hover for definitions
      </div>
      <div className="grid grid-cols-5 gap-2 text-[10px] md:text-xs font-mono-cal uppercase">
        {asc.map((r) => (
          <InfoTooltip
            key={r.n}
            title={`${r.n} · ${r.label}`}
            body={r.def}
            testId={`rating-legend-tip-${r.n}`}
          >
            <div
              tabIndex={0}
              className="border border-[#111] p-2 text-center cursor-help w-full hover:bg-[#111] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#FF4F00]"
              data-testid={`rating-legend-${r.n}`}
            >
              <div className="font-display text-xl">{r.n}</div>
              <div className="text-[9px] md:text-[10px] leading-tight mt-1">{r.short}</div>
            </div>
          </InfoTooltip>
        ))}
      </div>
    </div>
  );
}

export function ItemRow({ idx, kind, description, score, notes, onScore, onNotes, testPrefix }) {
  const noteLen = (notes || "").trim().length;
  const noteValid = noteLen >= MIN_NOTE_CHARS;
  const showNoteWarning = score && !noteValid;

  return (
    <div className="cal-card p-6" data-testid={`${testPrefix}-row-${idx}`}>
      <div className="flex items-start gap-4 mb-5">
        <div className="font-mono-cal text-xs text-[#FF4F00] pt-1">{String(idx + 1).padStart(2, "0")}</div>
        <div className="flex-1">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-1">{kind}</div>
          <div className="font-display font-medium text-lg leading-snug">{description}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 mb-4">
        {[1, 2, 3, 4, 5].map((n) => {
          const r = BY_N[n];
          return (
            <InfoTooltip
              key={n}
              title={`${r.n} · ${r.label}`}
              body={r.def}
              testId={`${testPrefix}-rate-tip-${idx}-${n}`}
            >
              <button
                type="button"
                onClick={() => onScore(n)}
                className={`rating-tile ${score === n ? "active" : ""}`}
                data-testid={`${testPrefix}-rate-${idx}-${n}`}
                aria-label={`${n} - ${r.label}`}
              >
                {n}
              </button>
            </InfoTooltip>
          );
        })}
      </div>
      <label className="block font-mono-cal text-[10px] uppercase tracking-widest mb-1" style={{ color: showNoteWarning ? "#FF4F00" : "#666" }}>
        Evidence / examples <span style={{ color: "#FF4F00" }}>*</span>
      </label>
      <textarea
        placeholder="Give specific examples that substantiate your rating — what happened, when, impact."
        rows={3}
        required
        minLength={MIN_NOTE_CHARS}
        value={notes || ""}
        onChange={(e) => onNotes(e.target.value)}
        className="w-full p-3 border focus:ring-2 focus:ring-[#FF4F00] outline-none resize-none text-sm"
        style={{ borderColor: showNoteWarning ? "#FF4F00" : "#111" }}
        data-testid={`${testPrefix}-notes-${idx}`}
      />
      <div className="flex items-center justify-between mt-1 text-[11px] font-mono-cal">
        <span style={{ color: showNoteWarning ? "#FF4F00" : "#666" }}>
          {showNoteWarning
            ? `At least ${MIN_NOTE_CHARS} characters of evidence required`
            : `Minimum ${MIN_NOTE_CHARS} characters`}
        </span>
        <span style={{ color: noteValid ? "#00A36C" : "#666" }}>
          {noteLen} / {MIN_NOTE_CHARS}{noteValid ? " ✓" : ""}
        </span>
      </div>
    </div>
  );
}
